import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { ApiCallCachingService } from '../../../services/api-call-caching.service';
import { DebounceTrigger } from '../../../models/debounce-trigger.model';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { switchMap, tap } from 'rxjs/operators';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';


@Component({
    selector: 'lib-dropdown-base',
    template: '',
    styleUrls: ['./dropdown-base.component.scss'],
    standalone: false
})

export abstract class DropdownBaseComponent<TValue, TListItem> implements OnInit {
  
  @Input() value : TValue | TValue[];
  @Output() valueChange = new EventEmitter<TValue | TValue[]>();
  @Output() nonPrimitiveValueChange = new EventEmitter<TListItem | TListItem[]>();
  @Output() userChangedValue = new EventEmitter<TValue | TValue[]>();

  private _disabled: boolean = false;
  public get disabled(): boolean {
    return this._disabled;
  }
  @Input() public set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;
    this.baseControlledDisabledState = isDisabled;
  }

  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  @Input() placeholder: string;
  @Input() defaultFilter: string;
  @Input() isMultiSelect: boolean = false;
  @Input() multiSelectSettings: { isGridFilter: boolean } = { isGridFilter: false };
  @Input() isReadOnlyOnSingleItem = false;
  @Input() smallView = false;
  @Input() noDataFoundText = "NO DATA FOUND";

  abstract get uniqueName(): string;

  @Input() abstract textField: string;
  @Input() abstract valueField: string;

  valuePrimitive = true;
  allowCustom = false;
  loadDataAutomatically = true;
  minimumFilterLength: number;
  isLoading: boolean = false;
  baseControlledDisabledState: boolean = false;

  @Input() abstract enableCache: boolean;

  data$: Observable<TListItem[]>;

  public filterSettings: DropDownFilterSettings;

  abstract dataFunction: (input: string | string[]) => Observable<TListItem[]>;

  dataFunctionWrapper = (input: string | string[]) => {
    if ((!this.minimumFilterLength) || input?.length >= this.minimumFilterLength) {
      if (this.enableCache) {
        if (this.data$) return; //if data is already present no need to get it from cache
        this.data$ = this.cachingService.getValue(this.uniqueName, this.dataFunction(input));
      }
      else {
        this.data$ = this.createReplaySubjectObservable(input); //Prevent unneeded backend API requests when using data$
      }
      
      this.handleIsLoading();
      this.handleReadOnlyOnSingleItem(input);
    }
  }

  debounceTrigger: DebounceTrigger<string> = new DebounceTrigger<string>(this.dataFunctionWrapper);

  constructor(private cachingService: ApiCallCachingService) { }

  ngOnInit(): void {
    if (this.loadDataAutomatically) {
        this.dataFunctionWrapper(this.defaultFilter);
        if (this.data$) {
            this.changeValue(this.value, false);
        }
    }
    if (this.enableCache) this.filterSettings = { caseSensitive: false, operator: 'contains' }; //if result is cached, enable frontend filter on string
  }

  async changeValue(value: TValue | TValue[], userAction = true) {
    this.value = value;
    if (this.valuePrimitive) {
      let data = (await this.data$.toPromise());
      let underlyingValue = Array.isArray(value) ? data.filter(x =>value.includes(x[this.valueField])) : data.find(x => x[this.valueField] === value);
      this.nonPrimitiveValueChange.emit(underlyingValue); //if the value is primitive, send the full underlying object in case we need more than only the mapped property
    } else {
      this.nonPrimitiveValueChange.emit(this.value as any);
    }


    this.valueChange.emit(this.value);
    if(userAction) {
      this.userChangedValue.emit(this.value);
    }
  }

  //By default we do not disable the multiselect items. Override this method to do so.
  itemDisabled(itemArgs: { dataItem: any; index: number }): boolean {
    return false;
  }

  protected createReplaySubjectObservable(input: string | string[]): Observable<TListItem[]> {
    if (this.dataFunction(input)) {
      let data = new ReplaySubject<TListItem[]>();
      this.dataFunction(input).subscribe(x => {
        data.next(x);
        data.complete();
      });
      return data?.asObservable();
    }

    return this.dataFunction(input);
  }

  private handleIsLoading() {
    if (this.data$) {
      this.data$.pipe((source) => of({}).pipe(tap(() => this.isLoading = true), switchMap((o) => source)))
       .subscribe(_ => { },
          _ => this.isLoading = false,
          () => this.isLoading = false);
    }
  }

  private handleReadOnlyOnSingleItem(input: string | string[]) {
    if (this.isReadOnlyOnSingleItem && !input) {
      //Prevent wacky stuff before the data is loaded and checked, we enable it again, if needed, after checking the data.
      this.baseControlledDisabledState = true;
      if (this.data$) {
        this.data$.subscribe(data => {
          if (data?.length === 1) {
            let singleItemValueField = this.valueField && this.valuePrimitive ? (<any>data[0])[this.valueField] : data[0];
            this.value = this.isMultiSelect ? [singleItemValueField] : singleItemValueField;
            this.changeValue(this.value);
          } else {
            this.baseControlledDisabledState = this.disabled;
          }
        });
      }
    }
  }
  
  showTooltip(e: MouseEvent): void {
    const targetElement = e.target as HTMLElement;

    this.tooltipDir.hide(); //Always hide

    if (targetElement.offsetWidth < targetElement.scrollWidth) {
      this.tooltipDir.toggle(targetElement);
    }
  }
}
